import SVPageMeta from '@apps/www/src/www/containers/SVPageMeta';
import SVA from '@pkgs/shared-client/components/SVA';
import {
	SVLegalPageNavContainer,
	Section,
	SectionTitle,
	Spacer,
	SubsectionTitle,
} from '@pkgs/shared-client/components/SVLegal';
const SVTermsPage = () => {
	const meta = {
		title: 'Terms of Service',
	};

	return (
		<>
			<SVPageMeta {...meta} />
			<SVLegalPageNavContainer>
				<Section>
					<div className="type-title font-semibold">Terms of Service</div>
				</Section>

				<Section>
					These Terms of Service ("Terms") govern your use of and access to any website,
					application, service, technology, API, widget, platform, channel or other
					products or features owned, operated, branded or offered by Savee ("Savee" or
					the "Service"), unless we explicitly state that different terms apply. When
					referring to "we" or "us" in these Terms, we mean the contracting party
					specified in Section 13(e) (Parties). Please review these Terms thoroughly, and
					reach out to us with any questions.
					<Spacer />
					Using the Service means you agree to follow these Terms. For reference, these
					Terms incorporate:
					<Spacer />
					<ul className="ml-5 list-inside list-disc">
						<li>
							Our Community Guidelines, which outline acceptable and unacceptable
							behavior on Savee
						</li>
						<li>
							Our Enforcement practices, which detail how we implement our policies,
							including potential restrictions on content or Service usage
						</li>
					</ul>
					<Spacer />
					By using Savee, you acknowledge and accept these Terms. If you disagree with
					these Terms, you must not use Savee.
				</Section>
				<Section>
					<SectionTitle>1. The Service</SectionTitle>
					Savee aims to connect people with inspiration that enriches their lives. We
					personalize your experience by showing content we believe you'll find meaningful
					and engaging based on how you interact with Savee and related services. To
					deliver this personalized experience, we process your personal information as
					detailed in our Privacy Policy. Some content you see may be sponsored by
					advertisers, but we strive to ensure even promotional content remains relevant
					to your interests. All sponsored content is clearly marked as such.
				</Section>
				<Section>
					<SectionTitle>2. Using Savee</SectionTitle>
					<SubsectionTitle>a. Eligibility and Access</SubsectionTitle>
					You can use Savee if you comply with these Terms and applicable laws. Using our
					Service may require downloading software to your devices. You agree that we can
					automatically update this software, and these Terms apply to all updates. When
					registering for Savee, you must provide accurate and complete information.
					<Spacer />
					Users under 13 years old may not use Savee. If you're between 13 and 18, you
					need your parent or legal guardian's permission and their review of these Terms.
					Users over 13 must also meet their country's minimum age requirements for
					consent.
					<Spacer />
					If we've previously suspended your account for violations, you cannot create a
					new account without our explicit written permission. We retain sole discretion
					in granting such permission.
					<Spacer />
					You agree not to:
					<Spacer />
					<ul className="ml-5 list-inside list-disc">
						<li>
							Extract data or content from Savee through unauthorized means, including
							automated methods (without our prior explicit permission)
						</li>
						<li>Access or attempt to access unauthorized data</li>
						<li>
							Use Savee in violation of trade restrictions, including US, EU, and UK
							sanctions and export controls
						</li>
						<li>Use the Service in ways not explicitly permitted by these Terms</li>
					</ul>
					<SubsectionTitle>b. License Terms</SubsectionTitle>
					We grant you a limited, non-exclusive, non-transferable, revocable license to
					use Savee according to these Terms and our policies. This license cannot be
					transferred or used by unauthorized third parties. We reserve all rights not
					explicitly granted, and you may not:
					<Spacer />
					<ul className="ml-5 list-inside list-disc">
						<li>Modify or create derivative works from the Service</li>
						<li>Reverse engineer the Service</li>
						<li>Copy Service features or functions</li>
					</ul>
				</Section>
				<Section>
					<SectionTitle>3. Your User Content</SectionTitle>
					<SubsectionTitle>a. Content Sharing</SubsectionTitle>
					Savee enables you to share various content, including images, videos, and
					comments (collectively, "User Content"). You maintain all rights to your User
					Content and are solely responsible for what you post on Savee. All shared
					content must comply with these Terms and our policies, including Community
					Guidelines.
					<Spacer />
					You warrant that you have the necessary rights to post your User Content and
					agree not to share content that infringes others' intellectual property rights
					(including copyright and trademark) or violates applicable laws, unless legal
					exceptions apply. We may take action against content that violates these Terms,
					our policies, or legal requirements by removing, limiting access to, or
					restricting distribution of such content. As a neutral intermediary platform, we
					don't pre-screen User Content, and therefore aren't liable to third parties for
					content accuracy or User Content posted by you or other users. For details on
					our enforcement practices, visit our Enforcement page. You may appeal decisions
					you believe were made in error. Local laws may grant you the right to pursue
					claims if you believe we've breached these Terms by taking action on your
					content or account. We cannot provide legal advice.
					<SubsectionTitle>b. Content License</SubsectionTitle>
					By sharing User Content, you grant Savee, our affiliates, service providers, and
					users a worldwide, non-exclusive, royalty-free, transferable, sublicensable
					license to use, store, display, perform, reproduce, modify, create derivatives,
					monetize, download, translate, and distribute your content, including for
					promotion and redistribution of Savee. This license doesn't: (i) entitle you to
					compensation or revenue sharing, or (ii) limit our other legal rights to User
					Content. We reserve the right to remove, limit distribution, or modify content,
					whether it violates our Terms, Community Guidelines, Copyright Policy, Trademark
					Policy, other policies, or for other reasonable platform interests.
					<SubsectionTitle>c. Content Retention</SubsectionTitle>
					After account termination, deactivation, or content removal, we may retain User
					Content for a reasonable period for backup, archival, or audit purposes. Users
					may continue to use, store, display, reproduce, share, modify, create
					derivatives from, perform, and distribute any content previously shared on
					Savee.
					<SubsectionTitle>d. User Feedback</SubsectionTitle>
					We welcome your suggestions for improving Savee. By submitting feedback, you
					acknowledge we have no obligation to keep it confidential or provide
					compensation. Please don't submit confidential information or third-party owned
					feedback. Our acceptance of feedback doesn't waive our rights to similar ideas
					we already knew about, developed internally, or received from other sources.
					<SubsectionTitle>e. Reporting System</SubsectionTitle>
					Our reporting tools let you flag content that may violate our Terms, policies,
					or local laws. We review reports promptly and take appropriate action. By using
					these tools, you agree to submit reports in good faith and not misuse reporting
					or appeals channels.
				</Section>
				<Section>
					<SectionTitle>4. Intellectual Property</SectionTitle>
					<SubsectionTitle>a. IP Policies</SubsectionTitle>
					We maintain and enforce comprehensive Copyright and Trademark Policies aligned
					with applicable intellectual property laws. Please review our separate Copyright
					Policy and Trademark Policy for details.
					<SubsectionTitle>b. Savee's IP Rights</SubsectionTitle>
					All Savee platform elements and related intellectual property rights (including
					copyrights, trademarks, and patents) ("Savee IP") belong to us. Your use of
					Savee IP must stay within permitted platform use. Unauthorized use terminates
					your platform access rights and may require return or destruction of any Savee
					IP copies. These Terms don't transfer ownership rights, and we reserve all
					rights not explicitly granted.
				</Section>
				<Section>
					<SectionTitle>5. Security</SectionTitle>
					Your security matters to us. While we implement robust security measures, we
					can't guarantee complete protection against unauthorized access. Protect your
					password, don't share it, and alert us immediately about unauthorized account
					access or security issues.
				</Section>
				<Section>
					<SectionTitle>6. External Links and Services</SectionTitle>
					Savee content may include links to external websites, advertisers, services,
					features, or events not under our control. We don't endorse or assume
					responsibility for external content, services, or products. Accessing external
					content through Savee is at your own risk, and we bear no liability for your use
					of or purchases from third-party sources.
				</Section>
				<Section>
					<SectionTitle>7. Account Termination</SectionTitle>
					We reserve the right to suspend or terminate your Savee access for Terms
					violations. When appropriate, we'll provide written notice and allow you to
					appeal decisions you believe were made in error. See our Enforcement page for
					details about our content and account action procedures. You may terminate your
					account at any time. After termination, Sections 3, 8, 9, 10, 11, 12, and 13 of
					these Terms remain in effect.
				</Section>
				<Section>
					<SectionTitle>8. Indemnification</SectionTitle>
					You agree to defend, indemnify, and hold harmless Savee, its affiliates,
					officers, directors, employees, and agents from any claims, damages, losses,
					liabilities, costs, and expenses (including reasonable legal fees) arising from:
					(a) your use of Savee; (b) your User Content; (c) your violation of these Terms
					or applicable laws; or (d) your violation of any third-party rights, including
					privacy or intellectual property rights.
				</Section>
				<Section>
					<SectionTitle>9. Disclaimers</SectionTitle>
					<p>
						To the extent permitted by law, Savee and its content are provided "as is"
						without warranties of any kind, express or implied.
					</p>
					<Spacer />
					<p>We specifically disclaim all warranties regarding:</p>
					<Spacer />
					<ul className="ml-5 list-inside list-disc">
						<li>Merchantability</li>
						<li>Fitness for particular purposes</li>
						<li>Non-infringement</li>
						<li>Warranties arising from business dealings or usage customs</li>
					</ul>
					<Spacer />
					<p>
						Savee assumes no responsibility for User Content posted through our Service.
						You acknowledge that you may encounter content that's inaccurate,
						inappropriate, or unsuitable for your needs.
					</p>
					<Spacer />
					<p>We don't guarantee that Savee will be:</p>
					<Spacer />
					<ul className="ml-5 list-inside list-disc">
						<li>Uninterrupted or error-free</li>
						<li>Free from harmful components</li>
						<li>Completely secure</li>
						<li>Entirely accurate or current</li>
					</ul>
					<Spacer />
					<p>
						We may modify Savee for any reason and aren't liable for changes,
						suspensions, or access termination. These limitations apply to the fullest
						extent permitted by law.
					</p>
				</Section>
				<Section>
					<SectionTitle>10. Liability Limitations</SectionTitle>
					<p>
						Unless prohibited by law, we aren't liable for indirect, incidental,
						special, consequential, or punitive damages, including lost profits, data,
						use, or goodwill resulting from:
					</p>
					<Spacer />
					<ul className="ml-5 list-inside list-disc">
						<li>Service access, use, or access inability</li>
						<li>Third-party conduct or content on Savee</li>
						<li>Completely secure</li>
						<li>Unauthorized access or content alterations</li>
					</ul>
					<Spacer />
					<p>
						Our total liability for all claims cannot exceed one hundred U.S. dollars
						($100.00), except where legally prohibited.
					</p>
					<Spacer />
					<p>
						For EEA or UK consumers, different rules apply. Our liability is limited to
						foreseeable damages from material contractual breaches. We maintain full
						liability for damages caused by gross negligence, willful misconduct, death,
						personal injury, or other legally mandated obligations.
					</p>
				</Section>
				<Section>
					<SectionTitle>11. Dispute Resolution</SectionTitle>
					<Spacer />
					<p>[EEA and UK consumers are exempt from this section]</p>
					<Spacer />
					<p className="font-semibold">Informal Resolution</p>
					<Spacer />
					<p>Before legal proceedings, you must:</p>
					<Spacer />
					<ol>
						<li>1. Contact us to attempt informal resolution</li>
						<li>
							2. Submit a detailed Notice of Dispute to{' '}
							<SVA href="mailto:litigation@savee.it">litigation@savee.it</SVA>{' '}
							including:
						</li>
						<Spacer />
						<ul className="ml-5 list-inside list-disc">
							<li>Full name and Savee profile</li>
							<li>Account email</li>
							<li>Country/state of residence</li>
							<li>Legal representation details (if any)</li>
							<li>Dispute description</li>
							<li>Alleged harm</li>
							<li>Signature </li>
						</ul>
					</ol>
					<Spacer />
					<p>We'll respond within 60 days.</p>
					<Spacer />
					<p className="font-semibold">Arbitration Agreement</p>
					<Spacer />
					<p>
						After this period, either party may initiate arbitration. Arbitration
						Agreement Unresolved disputes will be settled through binding arbitration or
						small claims court for qualifying cases. This agreement is governed by the
						U.S. Federal Arbitration Act. Both parties waive rights to jury trials and
						class actions.
					</p>
					<Spacer />
					<p>
						American Arbitration Association (AAA) rules apply, but these Terms prevail
						if conflicts arise. The arbitrator must provide a written decision
						explaining their reasoning. All proceedings must be individual actions, not
						class or representative proceedings.
					</p>
					<Spacer />
					<p className="font-semibold">Batch Processing</p>
					<Spacer />
					<p>
						For efficiency, similar arbitration demands (50+ within 60 days or from the
						same legal representation) may be consolidated. AAA will apply one set of
						fees per batch and appoint one arbitrator for consolidated resolution.
					</p>
				</Section>
				<Section>
					<SectionTitle>12. Governing Law</SectionTitle>
					<p>
						EEA/UK consumers: Your local laws govern these Terms, and local courts
						handle disputes.
					</p>
					<Spacer />
					<p>
						All others: California law governs these Terms. Los Angeles County,
						California, or the U.S. District Court for the Southern District of
						California has exclusive jurisdiction for non-arbitration disputes.
					</p>
				</Section>
				<Section>
					<SectionTitle>13. General Provisions</SectionTitle>
					<SubsectionTitle>a. Terms Updates</SubsectionTitle>
					We may modify these Terms, with the latest version posted on our website.
					Material changes require advance notice unless legally exempted. Continued
					Service use constitutes acceptance of updates. Disputes apply the Terms version
					in effect when they arose.
					<SubsectionTitle>b. Assignment Rights</SubsectionTitle>
					EEA/UK consumers may assign their rights. Others cannot transfer rights without
					our permission, but we may assign our rights freely. Assignment notifications
					will be provided, and you may terminate your account immediately following our
					assignment.
					<SubsectionTitle>c. Agreement Completeness</SubsectionTitle>
					These Terms constitute our complete agreement. Invalid provisions will be
					minimized or eliminated while maintaining other Terms provisions.
					<SubsectionTitle>d. Rights Preservation</SubsectionTitle>
					Not enforcing any provision doesn't waive our rights to future enforcement.
					<SubsectionTitle>e. Global Application</SubsectionTitle>
					These Terms benefit Savee Inc. and all worldwide subsidiaries and affiliates.
				</Section>
			</SVLegalPageNavContainer>
		</>
	);
};

export default SVTermsPage;
